import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import pb from "../../../lib/pb";


/*
==================================================
DELIVERY PARTNER MANAGEMENT
==================================================

Manager-side dashboard.

Responsibilities:

- Partner statistics
- Pending verification
- Approve / reject partner applications
- Active partner list
- Partner status
- Current delivery
- Current location
- Delivery history
- Performance

PocketBase collections used:

delivery_partners
deliveries
delivery_requests
delivery_bids
users
delivery_events
delivery_modes

The page intentionally keeps manager functionality
separate from the Delivery Partner application.
==================================================
*/


/* ==================================================
   HELPERS
================================================== */

function safeString(
  value,
  fallback = ""
) {

  return String(
    value ??
    fallback
  ).trim();

}


function normalizeStatus(
  value
) {

  return safeString(
    value
  )
    .toLowerCase()
    .replace(
      /\s+/g,
      "_"
    );

}


function displayStatus(
  value
) {

  const normalized =
    normalizeStatus(
      value
    );


  if (
    normalized ===
    "online"
  ) {

    return "Online";

  }


  if (
    normalized ===
    "busy"
  ) {

    return "Busy";

  }


  if (
    normalized ===
    "offline"
  ) {

    return "Offline";

  }


  if (
    normalized ===
    "suspended"
  ) {

    return "Suspended";

  }


  if (
    normalized ===
    "pending"
  ) {

    return "Pending";

  }


  if (
    normalized ===
    "verified"
  ) {

    return "Verified";

  }


  return value ||
    "Unknown";

}


function vehicleIcon(
  value
) {

  const mode =
    safeString(
      value
    ).toLowerCase();


  if (
    mode.includes(
      "bike"
    ) ||
    mode.includes(
      "motor"
    )
  ) {

    return "??";

  }


  if (
    mode.includes(
      "cycle"
    ) ||
    mode.includes(
      "bicycle"
    )
  ) {

    return "??";

  }


  if (
    mode.includes(
      "car"
    )
  ) {

    return "??";

  }


  if (
    mode.includes(
      "van"
    )
  ) {

    return "??";

  }


  if (
    mode.includes(
      "truck"
    )
  ) {

    return "??";

  }


  return "??";

}


function formatDate(
  value
) {

  if (
    !value
  ) {

    return "�";

  }


  const date =
    new Date(
      value
    );


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {

    return "�";

  }


  return date.toLocaleString(
    "en-IN",
    {
      day:
        "2-digit",
      month:
        "short",
      year:
        "numeric",
      hour:
        "2-digit",
      minute:
        "2-digit"
    }
  );

}


function getLocationText(
  delivery
) {

  const location =
    delivery?.currentLocation;


  if (
    !location
  ) {

    return "Location unavailable";

  }


  if (
    typeof location ===
    "string"
  ) {

    return location;

  }


  if (
    location?.lat !== undefined &&
    location?.lng !== undefined
  ) {

    return `${location.lat}, ${location.lng}`;

  }


  if (
    location?.latitude !== undefined &&
    location?.longitude !== undefined
  ) {

    return `${location.latitude}, ${location.longitude}`;

  }


  return "Location unavailable";

}


/* ==================================================
   COMPONENT
================================================== */

export default function DeliveryPartnerManagement() {

  const [
    partners,
    setPartners
  ] =
    useState(
      []
    );


  const [
    deliveries,
    setDeliveries
  ] =
    useState(
      []
    );


  const [
    loading,
    setLoading
  ] =
    useState(
      true
    );


  const [
    error,
    setError
  ] =
    useState(
      ""
    );


  const [
    actionLoading,
    setActionLoading
  ] =
    useState(
      ""
    );


  const [
    selectedPartner,
    setSelectedPartner
  ] =
    useState(
      null
    );


  const [
    search,
    setSearch
  ] =
    useState(
      ""
    );


  const [
    filter,
    setFilter
  ] =
    useState(
      "all"
    );


  /* ==================================================
     LOAD PARTNERS
  ================================================== */

  const loadPartners =
    useCallback(
      async () => {

        try {

          setError(
            ""
          );


          const records =
            await pb
              .collection(
                "delivery_partners"
              )
              .getFullList({
                sort:
                  "-created",
                expand:
                  "user,vehicleMode"
              });


          setPartners(
            records
          );

        }

        catch (err) {

          console.error(
            "Delivery partner management: partner loading failed:",
            err?.data ||
            err?.message ||
            err
          );


          setError(
            "Unable to load delivery partners."
          );

        }

      },
      []
    );


  /* ==================================================
     LOAD ACTIVE DELIVERIES
  ================================================== */

  const loadDeliveries =
    useCallback(
      async () => {

        try {

          const records =
            await pb
              .collection(
                "deliveries"
              )
              .getFullList({
                sort:
                  "-created",
                expand:
                  "partner,customer,request,order"
              });


          setDeliveries(
            records
          );

        }

        catch (err) {

          /*
          ----------------------------------------------
          Delivery records may not yet exist.

          The partner management page should still
          remain usable.
          ----------------------------------------------
          */

          console.error(
            "Delivery partner management: delivery loading failed:",
            err?.data ||
            err?.message ||
            err
          );


          setDeliveries(
            []
          );

        }

      },
      []
    );


  /* ==================================================
     INITIAL LOAD
  ================================================== */

  useEffect(
    () => {

      let mounted =
        true;


      async function load() {

        setLoading(
          true
        );


        await Promise.all([
          loadPartners(),
          loadDeliveries()
        ]);


        if (
          mounted
        ) {

          setLoading(
            false
          );

        }

      }


      load();


      return () => {

        mounted =
          false;

      };

    },
    [
      loadPartners,
      loadDeliveries
    ]
  );


  /* ==================================================
     REALTIME PARTNER UPDATES
  ================================================== */

  useEffect(
    () => {

      let unsubscribe;


      async function subscribe() {

        try {

          unsubscribe =
            await pb
              .collection(
                "delivery_partners"
              )
              .subscribe(
                "*",
                event => {

                  if (
                    event.action ===
                    "delete"
                  ) {

                    setPartners(
                      current =>
                        current.filter(
                          partner =>
                            partner.id !==
                            event.record.id
                        )
                    );


                    return;

                  }


                  setPartners(
                    current => {

                      const exists =
                        current.some(
                          partner =>
                            partner.id ===
                            event.record.id
                        );


                      if (
                        exists
                      ) {

                        return current.map(
                          partner =>
                            partner.id ===
                            event.record.id
                              ? event.record
                              : partner
                        );

                      }


                      return [
                        event.record,
                        ...current
                      ];

                    }
                  );

                }
              );

        }

        catch (err) {

          console.error(
            "Delivery partner realtime subscription failed:",
            err
          );

        }

      }


      subscribe();


      return () => {

        if (
          unsubscribe
        ) {

          unsubscribe();

        }


        pb
          .collection(
            "delivery_partners"
          )
          .unsubscribe(
            "*"
          );

      };

    },
    []
  );


  /* ==================================================
     REALTIME DELIVERY UPDATES
  ================================================== */

  useEffect(
    () => {

      let unsubscribe;


      async function subscribe() {

        try {

          unsubscribe =
            await pb
              .collection(
                "deliveries"
              )
              .subscribe(
                "*",
                event => {

                  if (
                    event.action ===
                    "delete"
                  ) {

                    setDeliveries(
                      current =>
                        current.filter(
                          delivery =>
                            delivery.id !==
                            event.record.id
                        )
                    );


                    return;

                  }


                  setDeliveries(
                    current => {

                      const exists =
                        current.some(
                          delivery =>
                            delivery.id ===
                            event.record.id
                        );


                      if (
                        exists
                      ) {

                        return current.map(
                          delivery =>
                            delivery.id ===
                            event.record.id
                              ? event.record
                              : delivery
                        );

                      }


                      return [
                        event.record,
                        ...current
                      ];

                    }
                  );

                }
              );

        }

        catch (err) {

          console.error(
            "Delivery realtime subscription failed:",
            err
          );

        }

      }


      subscribe();


      return () => {

        if (
          unsubscribe
        ) {

          unsubscribe();

        }


        pb
          .collection(
            "deliveries"
          )
          .unsubscribe(
            "*"
          );

      };

    },
    []
  );


  /* ==================================================
     DERIVED PARTNER DATA
  ================================================== */

  const pendingPartners =
    useMemo(
      () =>
        partners.filter(
          partner =>
            normalizeStatus(
              partner.verificationStatus
            ) ===
            "pending"
        ),
      [
        partners
      ]
    );


  const verifiedPartners =
    useMemo(
      () =>
        partners.filter(
          partner =>
            normalizeStatus(
              partner.verificationStatus
            ) ===
            "verified"
        ),
      [
        partners
      ]
    );


  const onlinePartners =
    useMemo(
      () =>
        partners.filter(
          partner =>
            normalizeStatus(
              partner.status
            ) ===
            "online"
        ),
      [
        partners
      ]
    );


  const busyPartners =
    useMemo(
      () =>
        partners.filter(
          partner =>
            normalizeStatus(
              partner.status
            ) ===
            "busy"
        ),
      [
        partners
      ]
    );


  const suspendedPartners =
    useMemo(
      () =>
        partners.filter(
          partner =>
            normalizeStatus(
              partner.status
            ) ===
            "suspended"
        ),
      [
        partners
      ]
    );


  /* ==================================================
     PARTNER SEARCH + FILTER
  ================================================== */

  const filteredPartners =
    useMemo(
      () => {

        const normalizedSearch =
          search
            .trim()
            .toLowerCase();


        return partners.filter(
          partner => {

            const vehicleMode =
              partner?.expand
                ?.vehicleMode;


            const searchableText = [
              partner?.fullName,
              partner?.phone,
              partner?.vehicleNumber,
              partner?.city,
              partner?.area,
              partner?.state,
              partner?.deliveryMode,
              vehicleMode?.name,
              vehicleMode?.code
            ]
              .map(
                value =>
                  safeString(
                    value
                  ).toLowerCase()
              )
              .join(
                " "
              );


            const matchesSearch =
              !normalizedSearch ||
              searchableText.includes(
                normalizedSearch
              );


            const status =
              normalizeStatus(
                partner.status
              );


            const verification =
              normalizeStatus(
                partner.verificationStatus
              );


            let matchesFilter =
              true;


            if (
              filter ===
              "pending"
            ) {

              matchesFilter =
                verification ===
                "pending";

            }


            if (
              filter ===
              "online"
            ) {

              matchesFilter =
                status ===
                "online";

            }


            if (
              filter ===
              "busy"
            ) {

              matchesFilter =
                status ===
                "busy";

            }


            if (
              filter ===
              "offline"
            ) {

              matchesFilter =
                status ===
                "offline";

            }


            if (
              filter ===
              "suspended"
            ) {

              matchesFilter =
                status ===
                "suspended";

            }


            return (
              matchesSearch &&
              matchesFilter
            );

          }
        );

      },
      [
        partners,
        search,
        filter
      ]
    );


  /* ==================================================
     DELIVERY LOOKUP
  ================================================== */

  const deliveryForPartner =
    useCallback(
      partnerId => {

        return (
          deliveries.find(
            delivery => {

              const deliveryPartner =
                delivery?.partner;


              const expandedPartner =
                delivery?.expand
                  ?.partner;


              return (
                String(
                  deliveryPartner ||
                  ""
                ) ===
                String(
                  partnerId
                ) ||
                String(
                  expandedPartner?.id ||
                  ""
                ) ===
                String(
                  partnerId
                )
              );

            }
          ) ||
          null
        );

      },
      [
        deliveries
      ]
    );


  /* ==================================================
     APPROVE PARTNER
  ================================================== */

  async function approvePartner(
    partner
  ) {

    if (
      !partner?.id
    ) {

      return;

    }


    setActionLoading(
      partner.id
    );


    try {

      await pb
        .collection(
          "delivery_partners"
        )
        .update(
          partner.id,
          {
            verificationStatus:
              "verified",
            status:
              "offline",
            acceptingOrders:
              false
          }
        );


      /*
      ------------------------------------------------
      Reload because the PocketBase update may not
      contain expanded relations.
      ------------------------------------------------
      */

      await loadPartners();


      if (
        selectedPartner?.id ===
        partner.id
      ) {

        const updated =
          await pb
            .collection(
              "delivery_partners"
            )
            .getOne(
              partner.id,
              {
                expand:
                  "user,vehicleMode"
              }
            );


        setSelectedPartner(
          updated
        );

      }

    }

    catch (err) {

      console.error(
        "Partner approval failed:",
        err?.data ||
        err?.message ||
        err
      );


      setError(
        err?.data?.message ||
        err?.message ||
        "Unable to approve delivery partner."
      );

    }

    finally {

      setActionLoading(
        ""
      );

    }

  }


  /* ==================================================
     REJECT PARTNER
  ================================================== */

  async function rejectPartner(
    partner
  ) {

    if (
      !partner?.id
    ) {

      return;

    }


    const confirmed =
      window.confirm(
        `Reject the delivery partner application for ${partner.fullName || "this partner"}?`
      );


    if (
      !confirmed
    ) {

      return;

    }


    setActionLoading(
      partner.id
    );


    try {

      await pb
        .collection(
          "delivery_partners"
        )
        .update(
          partner.id,
          {
            verificationStatus:
              "rejected",
            status:
              "offline",
            acceptingOrders:
              false
          }
        );


      await loadPartners();


      if (
        selectedPartner?.id ===
        partner.id
      ) {

        const updated =
          await pb
            .collection(
              "delivery_partners"
            )
            .getOne(
              partner.id,
              {
                expand:
                  "user,vehicleMode"
              }
            );


        setSelectedPartner(
          updated
        );

      }

    }

    catch (err) {

      console.error(
        "Partner rejection failed:",
        err?.data ||
        err?.message ||
        err
      );


      setError(
        err?.data?.message ||
        err?.message ||
        "Unable to reject delivery partner."
      );

    }

    finally {

      setActionLoading(
        ""
      );

    }

  }


  /* ==================================================
     SUSPEND / UNSUSPEND
  ================================================== */

  async function toggleSuspension(
    partner
  ) {

    if (
      !partner?.id
    ) {

      return;

    }


    const currentlySuspended =
      normalizeStatus(
        partner.status
      ) ===
      "suspended";


    setActionLoading(
      partner.id
    );


    try {

      await pb
        .collection(
          "delivery_partners"
        )
        .update(
          partner.id,
          currentlySuspended
            ? {
                status:
                  "offline",
                acceptingOrders:
                  false
              }
            : {
                status:
                  "suspended",
                acceptingOrders:
                  false
              }
        );


      await loadPartners();

    }

    catch (err) {

      console.error(
        "Partner suspension update failed:",
        err?.data ||
        err?.message ||
        err
      );


      setError(
        err?.data?.message ||
        err?.message ||
        "Unable to update partner status."
      );

    }

    finally {

      setActionLoading(
        ""
      );

    }

  }


  /* ==================================================
     PARTNER CARD
  ================================================== */

  function PartnerCard({
    partner,
    pending = false
  }) {

    const vehicleMode =
      partner?.expand
        ?.vehicleMode;


    const vehicleName =
      vehicleMode?.name ||
      partner?.deliveryMode ||
      "Vehicle";


    const delivery =
      deliveryForPartner(
        partner.id
      );


    const status =
      normalizeStatus(
        partner.status
      );


    return (
      <div
        className="delivery-partner-card"
        onClick={() =>
          setSelectedPartner(
            partner
          )
        }
      >

        <div
          className="delivery-partner-card-main"
        >

          <div
            className="delivery-partner-avatar"
          >
            {safeString(
              partner.fullName,
              "DP"
            )
              .split(
                " "
              )
              .slice(
                0,
                2
              )
              .map(
                word =>
                  word[0]
              )
              .join(
                ""
              )
              .toUpperCase()}

          </div>


          <div
            className="delivery-partner-card-info"
          >

            <div
              className="delivery-partner-name"
            >
              {partner.fullName ||
                "Unnamed partner"}
            </div>


            <div
              className="delivery-partner-meta"
            >

              <span>
                {vehicleIcon(
                  vehicleName
                )}
              </span>

              <span>
                {vehicleName}
              </span>

              {partner.vehicleNumber && (
                <>
                  <span>
                    �
                  </span>

                  <span>
                    {partner.vehicleNumber}
                  </span>
                </>
              )}

            </div>


            <div
              className="delivery-partner-location"
            >
              {partner.area ||
                "�"}

              {partner.city &&
                ` � ${partner.city}`}

            </div>

          </div>


          <div
            className={`delivery-partner-status status-${status || "unknown"}`}
          >

            <span
              className="delivery-status-dot"
            />

            {displayStatus(
              partner.status
            )}

          </div>

        </div>


        {delivery && (
          <div
            className="delivery-partner-current-delivery"
          >

            <span>
              Current delivery
            </span>

            <strong>
              {displayStatus(
                delivery.status
              )}
            </strong>

          </div>
        )}


        {pending && (
          <div
            className="delivery-partner-documents"
          >

            <div>
              ? ID
            </div>

            <div>
              ? Driving License
            </div>

            <div>
              ? Vehicle RC
            </div>

            <div>
              ? Selfie
            </div>

          </div>
        )}


        {pending && (
          <div
            className="delivery-partner-card-actions"
            onClick={event =>
              event.stopPropagation()
            }
          >

            <button
              type="button"
              className="delivery-action-approve"
              disabled={
                actionLoading ===
                partner.id
              }
              onClick={() =>
                approvePartner(
                  partner
                )
              }
            >
              {actionLoading ===
              partner.id
                ? "Processing..."
                : "Approve"}
            </button>


            <button
              type="button"
              className="delivery-action-reject"
              disabled={
                actionLoading ===
                partner.id
              }
              onClick={() =>
                rejectPartner(
                  partner
                )
              }
            >
              Reject
            </button>

          </div>
        )}

      </div>
    );

  }


  /* ==================================================
     DETAIL PANEL
  ================================================== */

  function PartnerDetailPanel() {

    if (
      !selectedPartner
    ) {

      return null;

    }


    const partner =
      selectedPartner;


    const vehicleMode =
      partner?.expand
        ?.vehicleMode;


    const vehicleName =
      vehicleMode?.name ||
      partner?.deliveryMode ||
      "Vehicle";


    const delivery =
      deliveryForPartner(
        partner.id
      );


    return (
      <div
        className="delivery-partner-detail-overlay"
        onClick={() =>
          setSelectedPartner(
            null
          )
        }
      >

        <aside
          className="delivery-partner-detail-panel"
          onClick={event =>
            event.stopPropagation()
          }
        >

          <div
            className="delivery-partner-detail-header"
          >

            <div>

              <div
                className="delivery-detail-eyebrow"
              >
                DELIVERY PARTNER
              </div>

              <h2>
                {partner.fullName ||
                  "Unnamed partner"}
              </h2>

            </div>


            <button
              type="button"
              className="delivery-detail-close"
              onClick={() =>
                setSelectedPartner(
                  null
                )
              }
            >
              �
            </button>

          </div>


          <div
            className="delivery-detail-status-row"
          >

            <span
              className={`delivery-partner-status status-${normalizeStatus(
                partner.status
              )}`}
            >

              <span
                className="delivery-status-dot"
              />

              {displayStatus(
                partner.status
              )}

            </span>


            <span>
              {displayStatus(
                partner.verificationStatus
              )}
            </span>

          </div>


          <section
            className="delivery-detail-section"
          >

            <h3>
              Partner
            </h3>


            <div
              className="delivery-detail-grid"
            >

              <div>
                <span>
                  Phone
                </span>

                <strong>
                  {partner.phone ||
                    "�"}
                </strong>
              </div>


              <div>
                <span>
                  Delivery mode
                </span>

                <strong>
                  {vehicleIcon(
                    vehicleName
                  )}{" "}
                  {vehicleName}
                </strong>
              </div>


              <div>
                <span>
                  Vehicle
                </span>

                <strong>
                  {partner.vehicleNumber ||
                    "�"}
                </strong>
              </div>


              <div>
                <span>
                  Area
                </span>

                <strong>
                  {partner.area ||
                    "�"}
                </strong>
              </div>


              <div>
                <span>
                  City
                </span>

                <strong>
                  {partner.city ||
                    "�"}
                </strong>
              </div>


              <div>
                <span>
                  State
                </span>

                <strong>
                  {partner.state ||
                    "�"}
                </strong>
              </div>

            </div>

          </section>


          <section
            className="delivery-detail-section"
          >

            <h3>
              Current location
            </h3>


            {delivery ? (
              <div
                className="delivery-detail-location"
              >

                <strong>
                  {getLocationText(
                    delivery
                  )}
                </strong>

                <span>
                  Updated{" "}
                  {formatDate(
                    delivery.updated
                  )}
                </span>

              </div>
            ) : (
              <div
                className="delivery-detail-empty"
              >
                No active delivery.
              </div>
            )}

          </section>


          <section
            className="delivery-detail-section"
          >

            <h3>
              Current delivery
            </h3>


            {delivery ? (
              <div
                className="delivery-detail-delivery"
              >

                <div>

                  <span>
                    Status
                  </span>

                  <strong>
                    {displayStatus(
                      delivery.status
                    )}
                  </strong>

                </div>


                <div>

                  <span>
                    ETA
                  </span>

                  <strong>
                    {delivery.etaMinutes !==
                    undefined
                      ? `${delivery.etaMinutes} min`
                      : "�"}
                  </strong>

                </div>


                <div>

                  <span>
                    Customer
                  </span>

                  <strong>
                    {delivery?.expand
                      ?.customer
                      ?.name ||
                      delivery?.customerName ||
                      "�"}
                  </strong>

                </div>

              </div>
            ) : (
              <div
                className="delivery-detail-empty"
              >
                No active delivery.
              </div>
            )}

          </section>


          <section
            className="delivery-detail-section"
          >

            <h3>
              Documents
            </h3>


            <div
              className="delivery-document-list"
            >

              <div>
                ? Identity document
              </div>

              <div>
                ? Driving license
              </div>

              <div>
                ? Vehicle RC
              </div>

              <div>
                ? Selfie
              </div>

            </div>

          </section>


          <section
            className="delivery-detail-section"
          >

            <h3>
              Emergency contact
            </h3>


            <div
              className="delivery-detail-grid"
            >

              <div>
                <span>
                  Name
                </span>

                <strong>
                  {partner.emergencyName ||
                    "�"}
                </strong>
              </div>


              <div>
                <span>
                  Phone
                </span>

                <strong>
                  {partner.emergencyPhone ||
                    "�"}
                </strong>
              </div>


              <div>
                <span>
                  Relation
                </span>

                <strong>
                  {partner.emergencyRelation ||
                    "�"}
                </strong>
              </div>

            </div>

          </section>


          <div
            className="delivery-detail-actions"
          >

            {normalizeStatus(
              partner.verificationStatus
            ) ===
              "pending" && (
              <>
                <button
                  type="button"
                  className="delivery-action-approve"
                  disabled={
                    actionLoading ===
                    partner.id
                  }
                  onClick={() =>
                    approvePartner(
                      partner
                    )
                  }
                >
                  Approve
                </button>


                <button
                  type="button"
                  className="delivery-action-reject"
                  disabled={
                    actionLoading ===
                    partner.id
                  }
                  onClick={() =>
                    rejectPartner(
                      partner
                    )
                  }
                >
                  Reject
                </button>
              </>
            )}


            {normalizeStatus(
              partner.verificationStatus
            ) ===
              "verified" && (
              <button
                type="button"
                className={
                  normalizeStatus(
                    partner.status
                  ) ===
                  "suspended"
                    ? "delivery-action-approve"
                    : "delivery-action-reject"
                }
                disabled={
                  actionLoading ===
                  partner.id
                }
                onClick={() =>
                  toggleSuspension(
                    partner
                  )
                }
              >
                {normalizeStatus(
                  partner.status
                ) ===
                "suspended"
                  ? "Unsuspend"
                  : "Suspend"}
              </button>
            )}

          </div>

        </aside>

      </div>
    );

  }


  /* ==================================================
     LOADING
  ================================================== */

  if (
    loading
  ) {

    return (
      <div
        className="delivery-partner-management"
      >

        <div
          className="delivery-management-loading"
        >
          Loading delivery partners...
        </div>

      </div>
    );

  }


  /* ==================================================
     RENDER
  ================================================== */

  return (
    <div
      className="delivery-partner-management"
    >

      {/* ================================================
          HEADER
      ================================================ */}

      <header
        className="delivery-management-header"
      >

        <div>

          <div
            className="delivery-management-eyebrow"
          >
            DELIVERY OPERATIONS
          </div>


          <h1>
            DELIVERY PARTNER MANAGEMENT
          </h1>


          <p>
            Kolkata � North Zone
          </p>

        </div>


        <button
          type="button"
          className="delivery-management-refresh"
          onClick={() => {
            loadPartners();
            loadDeliveries();
          }}
        >
          ? Refresh
        </button>

      </header>


      {/* ================================================
          ERROR
      ================================================ */}

      {error && (
        <div
          className="delivery-management-error"
        >
          {error}
        </div>
      )}


      {/* ================================================
          STATISTICS
      ================================================ */}

      <section
        className="delivery-management-stats"
      >

        <button
          type="button"
          className={`delivery-stat-card ${
            filter === "all"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setFilter(
              "all"
            )
          }
        >

          <span>
            Total
          </span>

          <strong>
            {partners.length}
          </strong>

        </button>


        <button
          type="button"
          className={`delivery-stat-card ${
            filter === "online"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setFilter(
              "online"
            )
          }
        >

          <span>
            Online
          </span>

          <strong>
            {onlinePartners.length}
          </strong>

        </button>


        <button
          type="button"
          className={`delivery-stat-card ${
            filter === "busy"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setFilter(
              "busy"
            )
          }
        >

          <span>
            Busy
          </span>

          <strong>
            {busyPartners.length}
          </strong>

        </button>


        <button
          type="button"
          className={`delivery-stat-card ${
            filter === "pending"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setFilter(
              "pending"
            )
          }
        >

          <span>
            Pending verification
          </span>

          <strong>
            {pendingPartners.length}
          </strong>

        </button>


        <button
          type="button"
          className={`delivery-stat-card ${
            filter === "suspended"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setFilter(
              "suspended"
            )
          }
        >

          <span>
            Suspended
          </span>

          <strong>
            {suspendedPartners.length}
          </strong>

        </button>

      </section>


      {/* ================================================
          PENDING VERIFICATION
      ================================================ */}

      {pendingPartners.length >
        0 && (
        <section
          className="delivery-management-section"
        >

          <div
            className="delivery-section-heading"
          >

            <div>

              <div
                className="delivery-section-eyebrow"
              >
                VERIFICATION
              </div>

              <h2>
                PENDING VERIFICATION
              </h2>

            </div>


            <span
              className="delivery-section-count"
            >
              {pendingPartners.length}
            </span>

          </div>


          <div
            className="delivery-partner-list"
          >

            {pendingPartners.map(
              partner => (
                <PartnerCard
                  key={
                    partner.id
                  }
                  partner={
                    partner
                  }
                  pending
                />
              )
            )}

          </div>

        </section>
      )}


      {/* ================================================
          ACTIVE PARTNERS
      ================================================ */}

      <section
        className="delivery-management-section"
      >

        <div
          className="delivery-section-heading"
        >

          <div>

            <div
              className="delivery-section-eyebrow"
            >
              PARTNERS
            </div>

            <h2>
              ACTIVE PARTNERS
            </h2>

          </div>


          <span
            className="delivery-section-count"
          >
            {filteredPartners.length}
          </span>

        </div>


        <div
          className="delivery-management-toolbar"
        >

          <div
            className="delivery-search"
          >

            <span>
              ?
            </span>

            <input
              type="text"
              value={
                search
              }
              onChange={event =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search partner, vehicle, area..."
            />

          </div>


          <select
            value={
              filter
            }
            onChange={event =>
              setFilter(
                event.target.value
              )
            }
          >

            <option value="all">
              All partners
            </option>

            <option value="online">
              Online
            </option>

            <option value="busy">
              Busy
            </option>

            <option value="offline">
              Offline
            </option>

            <option value="suspended">
              Suspended
            </option>

            <option value="pending">
              Pending verification
            </option>

          </select>

        </div>


        {filteredPartners.length >
        0 ? (
          <div
            className="delivery-partner-list"
          >

            {filteredPartners.map(
              partner => (
                <PartnerCard
                  key={
                    partner.id
                  }
                  partner={
                    partner
                  }
                />
              )
            )}

          </div>
        ) : (
          <div
            className="delivery-management-empty"
          >
            No delivery partners match the current filter.
          </div>
        )}

      </section>


      {/* ================================================
          OPERATIONAL FLOW
      ================================================ */}

      <section
        className="delivery-management-section"
      >

        <div
          className="delivery-section-heading"
        >

          <div>

            <div
              className="delivery-section-eyebrow"
            >
              AREA OPERATIONS
            </div>

            <h2>
              LIVE OPERATIONS
            </h2>

          </div>

        </div>


        <div
          className="delivery-management-operation-flow"
        >

          <div>
            <strong>
              Partner
            </strong>

            <span>
              Manage verified riders
            </span>
          </div>


          <div>
            ?
          </div>


          <div>
            <strong>
              Current location
            </strong>

            <span>
              Live delivery position
            </span>
          </div>


          <div>
            ?
          </div>


          <div>
            <strong>
              Current delivery
            </strong>

            <span>
              Active delivery status
            </span>
          </div>


          <div>
            ?
          </div>


          <div>
            <strong>
              Customer
            </strong>

            <span>
              Delivery recipient
            </span>
          </div>


          <div>
            ?
          </div>


          <div>
            <strong>
              Support
            </strong>

            <span>
              Operational assistance
            </span>
          </div>


          <div>
            ?
          </div>


          <div>
            <strong>
              History
            </strong>

            <span>
              Completed deliveries
            </span>
          </div>


          <div>
            ?
          </div>


          <div>
            <strong>
              Performance
            </strong>

            <span>
              Partner metrics
            </span>
          </div>

        </div>

      </section>


      {/* ================================================
          DETAIL PANEL
      ================================================ */}

      <PartnerDetailPanel />

    </div>
  );

}